/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import moment from "moment";
import {
	mapValues,
	orderBy,
	find,
	filter,
	some,
	uniq,
	sum
} from "lodash";
import { pluralize } from "humanize-plus";
import {
	EVENT_CLEAR_VALUE,
	EVENT_UNCLEAR_VALUE,
	EVENT_DETAILS_SUPPRESSION_TOGGLED,
	EVENT_SECTION_FIELD_VISIBILTY_CHANGE
} from "constants.js";
import {
	buildAnchoredTimeValue,
	normalizeTimeValue
} from "../../utils/timeValues";
import { isValueNullish } from "../../utils/valueUtilities";
import {
	CONTAINS_SCOPES,
	evaluateContains
} from "./contains-engine";

export const CONDITION_SCOPES = {
	State: "STATE",
	Time: "TIME",
	Date: "DATE",
	Latitude: "LATITUDE",
	Longitude: "LONGITUDE",
	...CONTAINS_SCOPES
};

const dateHelper = {
	setTime: (fieldValueDate, utcOffset, conditionValue) => {
		const datePart = fieldValueDate.substring(0, 10);

		return moment(`${datePart}T${conditionValue}`).utcOffset(utcOffset, true);
	}
};

const periodDatePoints = {
	BeginInclusive: "BeginInclusive",
	EndExclusive: "EndExclusive",
	OpensInclusive: "OpensInclusive",
	ClosesExclusive: "ClosesExclusive",
	Deadline: "Deadline"
};

const decomposePeriodConfiguration = (configuration) => {
	const parts = configuration.split(".");
	const scope = parts.length > 1 ? parts[0] : undefined;
	const field = parts.length > 1 ? parts[1] : parts[0];

	return { scope, field };
};

const getCurrentPeriodRange = (configuration, date, metadata) => {
	const dateMoment = moment(date);

	let currentPeriod = null;

	for(let i = 0; i < metadata.form.Seasons.length; i++) {
		const season = metadata.form.Seasons[i];
		if(season.Scope !== configuration.scope)
			continue;
		if(moment(season.BeginInclusive).isAfter(date) || moment(season.EndExclusive).isSameOrBefore(date))
			continue;

		const foundSeasonPeriod = season.Periods.find(p => {
			const beginMoment = moment(p.BeginInclusive);
			const endMoment = moment(p.EndExclusive);

			return beginMoment.isSameOrBefore(dateMoment) && endMoment.isAfter(dateMoment);
		});

		if(!!foundSeasonPeriod) {
			currentPeriod = foundSeasonPeriod;
			break;
		}
	}

	return currentPeriod;
};

const toUnix = (moment) => parseInt(moment.format("X"));

const timeValueToUnix = (value) => {
	const anchoredTimeValue = buildAnchoredTimeValue(value);

	return anchoredTimeValue === undefined ? undefined : toUnix(moment.parseZone(anchoredTimeValue));
};

function dateEvaluator(evaluator, condition, metadata, relevantFieldValues) {
	if(!relevantFieldValues?.length) {
		return false;
	}

	const compareToValue = find(relevantFieldValues, (v) => v.FieldId === (condition.CompareToField || {}).Id) || {};
	const fieldValue = find(relevantFieldValues, (v) => v.FieldId === (condition.Field || {}).Id) || {};

	if(!fieldValue.Date || (condition.CompareToField && !compareToValue.Date)) {
		return false;
	}

	const selectedDate = moment.parseZone(fieldValue.Date);
	const compareToDate = compareToValue.Date && moment.parseZone(compareToValue.Date);
	const now = moment();

	if(condition.Scope?.Code === CONDITION_SCOPES.Date) {
		selectedDate.milliseconds(0)
			.seconds(0)
			.minutes(0)
			.hours(0);

		now.milliseconds(0)
			.seconds(0)
			.minutes(0)
			.hours(0);

		if(compareToDate) {
			compareToDate.milliseconds(0)
				.seconds(0)
				.minutes(0)
				.hours(0);
		}
	}

	const values = {
		value: condition.Scope?.Code === CONDITION_SCOPES.Time ?
			dateHelper.setTime(fieldValue.Date, selectedDate.utcOffset(), condition.Value) :
			condition.Value,
		minValue: moment(now).add(condition.MinValue, "minutes"),
		maxValue: moment(now).add(condition.MaxValue, "minutes")
	};

	if(values.value === undefined) {
		values.value = moment(compareToDate);
	}
	else if(typeof values.value === "string") {
		const configuration = decomposePeriodConfiguration(values.value || "");
		if(configuration.field in periodDatePoints) {
			const currentPeriod = getCurrentPeriodRange(configuration, selectedDate, metadata);
			if(!!currentPeriod) {
				const currentPeriodFieldValue = currentPeriod[configuration.field];
				if(isValueNullish(currentPeriodFieldValue)) {
					return false;
				}

				const periodFieldTime = moment(currentPeriodFieldValue);

				return evaluator([toUnix(now)], { value: toUnix(periodFieldTime) }, metadata);
			}

			return false;
		}
	}

	const compareTo = mapValues(values, (v) => {
		if(v !== undefined) {
			const conditionAsMoment = moment(v, moment.ISO_8601, true);
			if(conditionAsMoment.isValid()) {
				return toUnix(conditionAsMoment);
			}
			else {
				return toUnix(moment(now).add(v, "m"));
			}
		}
	});

	const selectedDateInUnix = toUnix(selectedDate);

	if(condition.Scope?.Code === CONDITION_SCOPES.Time) {
		console.log("times", { selectedDateInUnix, compareTo, formattedCompareValue: values.value.format(), formattedValue: selectedDate.format() });
	}
	return evaluator(fieldValue.Date ? [selectedDateInUnix] : [], compareTo, metadata);
}

function timeEvaluator(evaluator, condition, metadata, relevantFieldValues) {
	const compareToValue = find(relevantFieldValues, (fieldValue) => fieldValue.FieldId === (condition.CompareToField || {}).Id) || {};
	const fieldValue = find(relevantFieldValues, (value) => value.FieldId === (condition.Field || {}).Id) || {};
	const selectedTime = timeValueToUnix(fieldValue.Time);
	const compareToTime = timeValueToUnix(compareToValue.Time);
	const compareTo = {
		value: condition.Value === undefined ? compareToTime : timeValueToUnix(condition.Value),
		minValue: timeValueToUnix(condition.MinValue),
		maxValue: timeValueToUnix(condition.MaxValue)
	};

	return evaluator(selectedTime === undefined ? [] : [selectedTime], compareTo, metadata);
}

const INTERSECTION_SCOPES = {
	MatchField: "MATCHFIELD",
	RangeBegin: "RANGEBEGIN",
	RangeEnd: "RANGEEND"
};

export const RULE_OPERATORS = {
	AND: "AND",
	OR: "OR",
	NAND: "NAND",
	NOR: "NOR",
	XOR: "XOR"
};

export const OPERATOR_ACTIONS = {
	AND: (...args) => Object.values(...args ?? []).every(v => v),
	OR: (...args) => Object.values(...args ?? []).some(v => v),
	NAND: (...args) => !Object.values(...args ?? []).every(v => v),
	NOR: (...args) => !Object.values(...args ?? []).some(v => v),
	XOR: (...args) => !!(sum(Object.values(...args ?? []).map(a => !!a ? 1 : 0)) === 1) // cascade xor - true if there one and only one true result
};

export const AGGREGATION_ACTIONS = {
	ALL: (...args) => Object.values(...args ?? []).every(v => !!v),
	ANY: (...args) => Object.values(...args ?? []).some(v => !!v),
	NONE:  (...args) => !Object.values(...args ?? []).some(v => !!v),
	SOME:  (...args) => Object.values(...args ?? []).some(v => !!v) && Object.values(...args ?? []).some(v => !v)
};

export const CONDITION_OPERATORS = {
	INCLUSIVE: "inclusive",
	INCLUSIVEEXCLUSIVE: "inclusiveExclusive",
	EXCLUSIVEINCLUSIVE: "exclusiveInclusive",
	EXCLUSIVE: "exclusive",
	GREATERTHAN: "greaterThan",
	GREATERTHANOREQUALTO: "greaterThanOrEqualTo",
	LESSTHAN: "lessThan",
	LESSTHANOREQUALTO: "lessThanOrEqualTo",
	EQUALTO: "equalTo",
	NOTEQUALTO: "notEqualTo",
	IS: "is",
	HAS: "has",
	ISNOT: "isNot",
	HASBEEN: "hasBeen",
	HASNOTBEEN: "hasNotBeen",
	ISTRANSITIONINGTO: "isTransitioningTo",
	ISNOTTRANSITIONINGTO: "isNotTransitioningTo",
	ISTRANSITIONINGFROM: "isTransitioningFrom",
	ISNOTTRANSITIONINGFROM: "isNotTransitioningFrom",
	WAS: "was",
	WASNOT: "wasNot",
	ISCURRENTLY: "isCurrently",
	ISNOTCURRENTLY: "isNotCurrently",
	WITHENTITY: "withEntity",
	ISONLINE: "isOnline",
	ISINTERACTIVE: "isInteractive",
	"EXPORT_QUEUE:ENQUEUE": "enqueue",
	"EXPORT_QUEUE:DEQUEUE": "dequeue",
	"EXPORT_STATE:COMMITTED": "committed",
	"EXPORT_STATE:SUPPRESSED": "suppressed",
	"EXPORT_STATE:INTDETERMINATE": "indeterminate"
};

export const CONDITION_EVALUATIONS = {
	greaterThan: "GREATERTHAN",
	greaterThanOrEqualTo: "GREATERTHANOREQUALTO",
	lessThan: "LESSTHAN",
	lessThanOrEqualTo: "LESSTHANOREQUALTO"
};

export const EVALUATE_ON = {
	ONSUBMIT: "onSubmit",
	ONSAVE: "onSave"
};

const compareVals = (values, comparison, comparator, defaultOnEmpty = false) => {
	if(!values.length || values[0].isNotAvailable)
		return defaultOnEmpty;

	if(typeof values[0] === "object") {
		if(!!values[0].compareVal && !!comparison.value && !!comparison.value.compareVal) {
			const evaluated = comparator(values[0].compareVal, comparison.value.compareVal);
			return evaluated;
		}
		else if(!!values[0].Code) {
			return comparator(values[0].Code, comparison.value);
		}
		else if(values[0].value !== undefined) {
			return comparator(values[0].value, comparison.value);
		}
		else if(values[0].Number !== undefined) {
			return comparator(values[0].Number, comparison.value);
		}

	}
	return comparator(values[0], comparison.value);
};

const compareRoleVals = (metadata, comparison, comparator) => {
	const stateRoles = metadata.form.Security.Assigned ?? [];

	const comparedResult = stateRoles.some(r => comparator(r.Code, comparison.value, comparator));

	return comparedResult;
};

const compareFunctions = {
	greaterThan: (fieldValues, compareTo, metadata) =>
		compareVals(fieldValues, compareTo, (a, b) => parseFloat(a) > parseFloat(b)),
	greaterThanOrEqualTo: (fieldValues, compareTo, metadata) =>
		compareVals(fieldValues, compareTo, (a, b) => parseFloat(a) >= parseFloat(b)),
	lessThan: (fieldValues, compareTo, metadata) =>
		compareVals(fieldValues, compareTo, (a, b) => parseFloat(a) < parseFloat(b)),
	lessThanOrEqualTo: (fieldValues, compareTo, metadata) =>
		compareVals(fieldValues, compareTo, (a, b) => parseFloat(a) <= parseFloat(b)),
	equalTo: (fieldValues, compareTo, metadata) =>
		compareVals(fieldValues, compareTo, (a, b) => a === b),
	notEqualTo: (fieldValues, compareTo, metadata) =>
		compareVals(fieldValues, compareTo, (a, b) => a !== b)
};

const getOrderedReportHistory = (metadata) => {
	return orderBy(metadata?.report?.History ?? [], h => moment(h.Occurred));
};

const getCurrentReportStateCode = (metadata) => {
	const orderedHistory = getOrderedReportHistory(metadata);
	return orderedHistory[orderedHistory.length - 1]?.State?.Code;
};

const getPreviousReportStateCode = (metadata) => {
	const orderedHistory = getOrderedReportHistory(metadata);
	return orderedHistory.length < 2 ? undefined : orderedHistory[orderedHistory.length - 2]?.State?.Code;
};

const completedReportStateCodes = [
	"NEGATIVE",
	"SUBMITTED",
	"DELETED"
];

const isCompletedReportState = (stateCode) => completedReportStateCodes.indexOf(stateCode) > -1;

const getHistoryBeforeCurrent = (metadata) => {
	const orderedHistory = getOrderedReportHistory(metadata);
	return orderedHistory.slice(0, Math.max(0, orderedHistory.length - 1));
};

const getScopedExports = (metadata, configurationCode) => {
	const normalizedConfigurationCode = `${configurationCode ?? ""}`.toUpperCase();
	if(!normalizedConfigurationCode) {
		return [];
	}

	return orderBy(
		metadata?.report?.Exports ?? [],
		[
			exportReport => moment(exportReport.Log?.Created ?? 0).valueOf(),
			exportReport => exportReport.Log?.Id ?? 0,
			exportReport => exportReport.Id ?? 0
		])
		.filter(exportReport => exportReport.Log?.Configuration?.Code?.toUpperCase() === normalizedConfigurationCode);
};

const getExportQueueAction = (metadata, configurationCode) => {
	const current = getCurrentReportStateCode(metadata);
	const previous = getPreviousReportStateCode(metadata);

	if(current === "DELETED") {
		const wasTheReportEverCompleted = getHistoryBeforeCurrent(metadata)
			.some(history => isCompletedReportState(history.State?.Code));

		if(!wasTheReportEverCompleted) {
			return "NONE";
		}

		const hasTheReportBeenExported = getScopedExports(metadata, configurationCode)
			.some(exportReport => exportReport.IsCommitted === true);

		return hasTheReportBeenExported ? "ENQUEUE" : "DEQUEUE";
	}

	if(isCompletedReportState(current)) {
		return "ENQUEUE";
	}

	if(isCompletedReportState(previous)) {
		return "DEQUEUE";
	}

	return "NONE";
};

const isExportState = (exportReport, operatorCode) => {
	switch(operatorCode) {
		case "EXPORT_STATE:COMMITTED":
			return exportReport.IsCommitted === true;
		case "EXPORT_STATE:SUPPRESSED":
			return exportReport.IsSuppressed === true;
		case "EXPORT_STATE:INTDETERMINATE":
			return exportReport.IsCommitted !== true && exportReport.IsSuppressed !== true;
		default:
			return false;
	}
};

export const ENGINE_EVALUATIONS = {
	range: {
		code: "RANGE",
		inclusive: (fieldValues, compareTo, metadata) =>
			compareVals(fieldValues, { value: compareTo.minValue }, (a, b) => parseFloat(a) >= parseFloat(b)) &&
			compareVals(fieldValues, { value: compareTo.maxValue }, (a, b) => parseFloat(a) <= parseFloat(b)),
		inclusiveExclusive: (fieldValues, compareTo, metadata) =>
			compareVals(fieldValues, { value: compareTo.minValue }, (a, b) => parseFloat(a) >= parseFloat(b)) &&
			compareVals(fieldValues, { value: compareTo.maxValue }, (a, b) => parseFloat(a) < parseFloat(b)),
		exclusiveInclusive: (fieldValues, compareTo, metadata) =>
			compareVals(fieldValues, { value: compareTo.minValue }, (a, b) => parseFloat(a) > parseFloat(b)) &&
			compareVals(fieldValues, { value: compareTo.maxValue }, (a, b) => parseFloat(a) <= parseFloat(b)),
		exclusive: (fieldValues, compareTo, metadata) =>
			compareVals(fieldValues, { value: compareTo.minValue }, (a, b) => parseFloat(a) > parseFloat(b)) &&
			compareVals(fieldValues, { value: compareTo.maxValue }, (a, b) => parseFloat(a) < parseFloat(b))
	},
	compare: {
		code: "COMPARE",
		...compareFunctions
	},
	detailsCount: {
		code: "DETAILS:COUNT",
		...compareFunctions
	},
	location: {
		code: "LOCATION",
		...compareFunctions
	},
	any: {
		code: "ANY",
		evaluate: (fieldValues, compareTo, metadata) => {
			// fieldValues is { [detailId]: { [fieldId]: fieldValue[] }}

			const valueEntries = Object.values(fieldValues);

			for(const valuesForDetail of valueEntries) {
				const detailValuesArray = Object.values(valuesForDetail);
				const hasAllValues = detailValuesArray.length > 0 && Object.values(detailValuesArray).every(fv => fv.length > 0);

				if(hasAllValues) {
					return true;
				}
			}

			return false;
		}
	},
	required: {
		code: "REQUIRED",
		evaluate: (fieldValues, compareTo, metadata) =>
			(fieldValues.length > 0)
			&& fieldValues.every(v =>
				v.idRequired !== true ||
				(v.DataId !== undefined && v.DataId !== null) ||
				(v.EntityId !== undefined && v.EntityId !== null) ||
				v === true ||
				v === false
			)
	},
	value: {
		code: "VALUE",
		evaluate: (fieldValues, compareTo, metadata) =>
			ENGINE_EVALUATIONS.required.evaluate(fieldValues, compareTo, metadata)
			|| ENGINE_EVALUATIONS.notAvailable.evaluate(fieldValues, compareTo, metadata)
			|| ENGINE_EVALUATIONS.customEntry.evaluate(fieldValues, compareTo, metadata)
	},
	notAvailable: {
		code: "NOTAVAILABLE",
		evaluate: (fieldValues, compareTo, metadata) => {
			return fieldValues.length && fieldValues[0].IsNotAvailable === true;
		},
		withEntity: (fieldValues, compareTo, metadata) => {
			const isValuePresent = fieldValues.length && fieldValues[0].IsNotAvailable === true
				&& (fieldValues[0].DataId !== undefined || fieldValues[0].EntityId !== undefined);

			return isValuePresent;
		}
	},
	classification: {
		code: "CLASSIFICATION",
		is: (fieldValues, compareTo, metadata) =>
			!!(fieldValues.length) && (!!fieldValues[0].Classifications && some(fieldValues[0].Classifications, (c) => c.Code == compareTo.value)),
		isNot: (fieldValues, compareTo) =>
			!(fieldValues) || (fieldValues.length === 0) || (!fieldValues[0].Classifications) || (!some(fieldValues[0].Classifications, (c) => c.Code == compareTo.value))
	},
	identifier: {
		code: "IDENTIFIER",
		is: (fieldValues, compareTo, metadata) =>
			!!(fieldValues.length) && (!!fieldValues[0].Identifiers && some(fieldValues[0].Identifiers, (c) => c.Type.Code == compareTo.value)),
		isNot: (fieldValues, compareTo, metadata) =>
			!(fieldValues) || (fieldValues.length === 0) || (!fieldValues[0].Identifiers) || (!some(fieldValues[0].Identifiers, (c) => c.Type.Code == compareTo.value)),
		has: (fieldValues, compareTo, metadata) => {
			if(!(fieldValues.length) || !(compareTo.value) || !(compareTo.compareToFieldValue)) {
				return false;
			}

			const probeIdentifiers = fieldValues.flatMap(v => v.Identifiers?.filter(i => i.IsAcitive !== false && i.Type.Code === compareTo.value) ?? []);
			if(probeIdentifiers.length === 0) {
				return false;
			}

			const compareToFieldActiveIdentifiers = compareTo.compareToFieldValue.Identifiers?.filter(i => i.IsActive !== false) ?? [];

			const matches = probeIdentifiers.some(i =>
				compareTo.compareToFieldValue.allCodes.indexOf(i.Value) > -1 // match any friendly, primary, or otherwise nested entity code
				|| compareTo.compareToFieldValue.externalKeys.indexOf(i.Value) > -1 // match any friendly, primary, or otherwise nested externalKey
				|| compareToFieldActiveIdentifiers.some(ci => ci.Value === i.Value) // match any identifier value on compareTo field
			);

			return matches;
		}
	},
	regex: {
		code: "REGEX",
		is: (fieldValues, compareTo, metadata) => {
			if(!fieldValues.length)
				return false;

			const exp = new RegExp(compareTo.value);
			const isMatch = fieldValues.length > 0 && exp.test(fieldValues[0].String);
			return isMatch;
		},
		isNot: (fieldValues, compareTo, metadata) => {
			if(!fieldValues.length)
				return true;

			const exp = new RegExp(compareTo.value);
			const isMatch = fieldValues.length > 0 && exp.test(fieldValues[0].String);
			return !isMatch;
		}
	},
	empty: {
		code: "EMPTY",
		evaluate: (fieldValues, compareTo, metadata) =>
			fieldValues.length === 0
	},
	length: {
		code: "LENGTH",
		lessThanOrEqualTo: (fieldValues, compareTo, metadata) =>
			fieldValues.length && (fieldValues[0].String.length <= compareTo.value)
	},
	change: {
		code: "CHANGE",
		evaluate: () => false
	},
	remote: {
		code: "REMOTE",
		evaluate: () => false
	},
	contains: {
		code: "CONTAINS",
		// since this engine routes through evaluateAllConditions, there are not separate evaluators by operator
		// - the operators are handled in the evaluateContains function
		evaluate: evaluateContains 
	},
	intersect: {
		code: "INTERSECT",
		evaluate: (detailValues, compareTo, metadata) => {
			const fieldIdsByScope = metadata.rule.Conditions.reduce((acc, c) => {
				acc[c.Scope.Code] = c.Field.Id;
				
				return acc;
			}, {});

			const detailValueEntries = Object.entries(detailValues);
			for(let i = 0 ; i < detailValueEntries.length; i++) {
				const thisDetailValue = detailValueEntries[i][1];
				const thisDetailId = detailValueEntries[i][0];

				if(isValueNullish(thisDetailId) || thisDetailId === "undefined") { // undefined as an object entry key is "undefined", thanks js.
					continue; // skip header values - we only want to compare detail values
				}

				const matchFieldValue = thisDetailValue[fieldIdsByScope[INTERSECTION_SCOPES.MatchField]]?.[0];
			
				if(isValueNullish(matchFieldValue)) {
					return false;
				}

				for(let j = i + 1; j < detailValueEntries.length; j++) {
					const singleComparisonDetailFieldValueByFieldId = detailValueEntries[j][1];
					const comparisonDetailId = detailValueEntries[j][0];

					if(isValueNullish(comparisonDetailId) || comparisonDetailId === "undefined") { // undefined as an object entry key is "undefined", thanks js.
						continue; // skip header values - we only want to compare detail values
					}

					const comparisonMatchFieldValue = singleComparisonDetailFieldValueByFieldId[fieldIdsByScope[INTERSECTION_SCOPES.MatchField]]?.[0];
					const valuesMatch =
						(!isValueNullish(matchFieldValue.EntityId) && comparisonMatchFieldValue?.EntityId === matchFieldValue.EntityId)
						|| (!isValueNullish(matchFieldValue.DataId) && comparisonMatchFieldValue?.DataId === matchFieldValue.DataId);

					if(!valuesMatch) {
						continue;
					}

					const thisRangeBeginValue = new Date(thisDetailValue[fieldIdsByScope[INTERSECTION_SCOPES.RangeBegin]]?.[0].Date);
					const thisRangeEndValue = new Date(thisDetailValue[fieldIdsByScope[INTERSECTION_SCOPES.RangeEnd]]?.[0].Date);
					const comparisonRangeBeginValue = new Date(singleComparisonDetailFieldValueByFieldId[fieldIdsByScope[INTERSECTION_SCOPES.RangeBegin]]?.[0].Date);
					const comparisonRangeEndValue = new Date(singleComparisonDetailFieldValueByFieldId[fieldIdsByScope[INTERSECTION_SCOPES.RangeEnd]]?.[0].Date);

					if(!(thisRangeEndValue < comparisonRangeBeginValue || thisRangeBeginValue > comparisonRangeEndValue)) {
						console.log("intersection found", { detailValues, metadata, thisRangeBeginValue, thisRangeEndValue, comparisonRangeBeginValue, comparisonRangeEndValue });
						return false;
					}
				}
			}

			console.log("no intersection found", { detailValues, metadata, fieldIdsByScope });

			return true;
		}
	},
	environment: {
		code: "ENVIRONMENT",
		isOnline: (fieldValue, _, metadata) => fieldValue === metadata.isOnline,
		isInteractive: (fieldValue) => fieldValue === true
	},
	customEntry: {
		code: "CUSTOMENTRY",
		evaluate: (fieldValues, compareTo, metadata) =>
			fieldValues.length && (fieldValues[0].String && fieldValues[0].String.length > 0)
	},
	period: {
		code: "PERIOD",
		greaterThan: (fieldValues, compareTo, metadata) =>
			ENGINE_EVALUATIONS.compare.greaterThan(fieldValues, compareTo, metadata),
		greaterThanOrEqualTo: (fieldValues, compareTo, metadata) =>
			ENGINE_EVALUATIONS.compare.greaterThanOrEqualTo(fieldValues, compareTo, metadata),
		lessThan: (fieldValues, compareTo, metadata) =>
			ENGINE_EVALUATIONS.compare.lessThan(fieldValues, compareTo, metadata),
		lessThanOrEqualTo: (fieldValues, compareTo, metadata) =>
			ENGINE_EVALUATIONS.compare.lessThanOrEqualTo(fieldValues, compareTo, metadata),
		equalTo: (fieldValues, compareTo, metadata) =>
			ENGINE_EVALUATIONS.compare.equalTo(fieldValues, compareTo, metadata),
		notEqualTo: (fieldValues, compareTo, metadata) =>
			ENGINE_EVALUATIONS.compare.notEqualTo(fieldValues, compareTo, metadata)
	},
	unique: {
		code: "UNIQUE",
		evaluate: (detailValues, compareTo, metadata) => {
			// detailValues is { [detailId]: { [fieldId]: fieldValue[] }}
			const detailStrings = [];
			for(const detailFieldValuesByFieldId of Object.values(detailValues)) {
				const detailFieldValueEntries = Object.entries(detailFieldValuesByFieldId);
				const nonHeaderValuesByFieldId = detailFieldValueEntries.filter(([_, d]) => d.every(v => v.DetailId !== undefined));

				// web cannot evaluate UNIQUE over exclusively header values as they require knowledge of all reports in the system
				if(nonHeaderValuesByFieldId.length === 0) {
					return true;
				}

				let thisDetailString = "";

				detailFieldValueEntries.forEach(([fieldId, fieldVal]) => {
					if(fieldVal.length !== 1)
						; // do nothing - we do not support uniques over multi-entry fields currently

					let thisFieldValue = fieldVal[0];
					if(!thisFieldValue)
						;
					else if(!isValueNullish(thisFieldValue.Number)) {
						const optionId = thisFieldValue.DataId ?? thisFieldValue.EntityId;
						if(!isValueNullish(optionId)) {
							thisFieldValue = `${thisFieldValue.Number}|${optionId}`;
						}
						else {
							thisFieldValue = thisFieldValue.Number;
						}
					}
					else if(thisFieldValue.Time) {
						thisFieldValue = thisFieldValue.Time;
					}
					else if(thisFieldValue.EntityId) {
						if(thisFieldValue.ContactId) {
							thisFieldValue = `${thisFieldValue.EntityId}|${thisFieldValue.ContactId}`;
						}
						else {
							thisFieldValue = thisFieldValue.EntityId;
						}
					}
					else if(thisFieldValue.DataId !== undefined && thisFieldValue.DataId !== null) {
						thisFieldValue = thisFieldValue.DataId;
					}
					else if(thisFieldValue.compareVal) {
						thisFieldValue = thisFieldValue.compareVal;
					}

					thisDetailString = `${thisDetailString}|${fieldId}:${JSON.stringify(thisFieldValue)}`;
				});

				detailStrings.push(thisDetailString);
			}

			const uniqueRowVals = uniq(detailStrings);

			return uniqueRowVals.length === detailStrings.length || detailStrings.length === 0;
		},
		isCurrently: (detailValues, compareTo, metadata) => {
			/// short circuit this to false; only used in state based unique engines which are not supported on web
			/// but we still need to provide a function for the interface so that the rule won't get removed 
			/// and it can be returned from the api
			return false;
		}
	},
	state: {
		code: "STATE",
		hasBeen: (stateValue, compareTo, metadata) => {
			if(stateValue === undefined || stateValue === null) {
				return false;
			}
			if(!metadata?.report?.History || metadata.report.History.length === 0) {
				return false;
			}

			const hasValue = some(metadata.report.History, h => h.State.Code === stateValue);
			return hasValue;
		},
		hasNotBeen: (stateValue, compareTo, metadata) => {
			if(stateValue === undefined || stateValue === null) {
				return false;
			}
			if(!metadata?.report?.History || metadata.report.History.length === 0) {
				return true;
			}

			const hasValue = some(metadata.report.History, h => h.State.Code === stateValue);
			return !hasValue;
		},
		isTransitioningTo: () => false,
		isNotTransitioningTo: () => false,
		isTransitioningFrom: () => false,
		isNotTransitioningFrom: () => false,
		was: (stateValue, compareTo, metadata) => {
			if(stateValue === undefined || stateValue === null) {
				return false;
			}

			return getPreviousReportStateCode(metadata) === stateValue;
		},
		wasNot: (stateValue, compareTo, metadata) => {
			if(stateValue === undefined || stateValue === null) {
				return false;
			}

			return getPreviousReportStateCode(metadata) !== stateValue;
		},
		isCurrently: (stateValue, compareTo, metadata) => {
			if(stateValue === undefined || stateValue === null) {
				return false;
			}

			return getCurrentReportStateCode(metadata) === stateValue;
		},
		isNotCurrently: (stateValue, compareTo, metadata) => {
			if(stateValue === undefined || stateValue === null) {
				return false;
			}

			return getCurrentReportStateCode(metadata) !== stateValue;
		}
	},
	exportShould: {
		code: "EXPORT:SHOULD",
		enqueue: (configurationCode, compareTo, metadata) =>
			getExportQueueAction(metadata, configurationCode) === "ENQUEUE",
		dequeue: (configurationCode, compareTo, metadata) =>
			getExportQueueAction(metadata, configurationCode) === "DEQUEUE"
	},
	exportLatest: {
		code: "EXPORT:LATEST",
		committed: (configurationCode, compareTo, metadata) => {
			const scopedExports = getScopedExports(metadata, configurationCode);
			const latestExport = scopedExports[scopedExports.length - 1];

			return !!latestExport && isExportState(latestExport, "EXPORT_STATE:COMMITTED");
		},
		suppressed: (configurationCode, compareTo, metadata) => {
			const scopedExports = getScopedExports(metadata, configurationCode);
			const latestExport = scopedExports[scopedExports.length - 1];

			return !!latestExport && isExportState(latestExport, "EXPORT_STATE:SUPPRESSED");
		},
		indeterminate: (configurationCode, compareTo, metadata) => {
			const scopedExports = getScopedExports(metadata, configurationCode);
			const latestExport = scopedExports[scopedExports.length - 1];

			return !!latestExport && isExportState(latestExport, "EXPORT_STATE:INTDETERMINATE");
		}
	},
	exportPreviously: {
		code: "EXPORT:PREVIOUSLY",
		committed: (configurationCode, compareTo, metadata) => {
			const scopedExports = getScopedExports(metadata, configurationCode);
			const previousExport = scopedExports.length < 2 ? undefined : scopedExports[scopedExports.length - 2];

			return !!previousExport && isExportState(previousExport, "EXPORT_STATE:COMMITTED");
		},
		suppressed: (configurationCode, compareTo, metadata) => {
			const scopedExports = getScopedExports(metadata, configurationCode);
			const previousExport = scopedExports.length < 2 ? undefined : scopedExports[scopedExports.length - 2];

			return !!previousExport && isExportState(previousExport, "EXPORT_STATE:SUPPRESSED");
		},
		indeterminate: (configurationCode, compareTo, metadata) => {
			const scopedExports = getScopedExports(metadata, configurationCode);
			const previousExport = scopedExports.length < 2 ? undefined : scopedExports[scopedExports.length - 2];

			return !!previousExport && isExportState(previousExport, "EXPORT_STATE:INTDETERMINATE");
		}
	},
	exportAny: {
		code: "EXPORT:ANY",
		committed: (configurationCode, compareTo, metadata) =>
			getScopedExports(metadata, configurationCode).some(exportReport => isExportState(exportReport, "EXPORT_STATE:COMMITTED")),
		suppressed: (configurationCode, compareTo, metadata) =>
			getScopedExports(metadata, configurationCode).some(exportReport => isExportState(exportReport, "EXPORT_STATE:SUPPRESSED")),
		indeterminate: (configurationCode, compareTo, metadata) =>
			getScopedExports(metadata, configurationCode).some(exportReport => isExportState(exportReport, "EXPORT_STATE:INTDETERMINATE"))
	},
	exportNever: {
		code: "EXPORT:NEVER",
		committed: (configurationCode, compareTo, metadata) =>
			!getScopedExports(metadata, configurationCode).some(exportReport => isExportState(exportReport, "EXPORT_STATE:COMMITTED")),
		suppressed: (configurationCode, compareTo, metadata) =>
			!getScopedExports(metadata, configurationCode).some(exportReport => isExportState(exportReport, "EXPORT_STATE:SUPPRESSED")),
		indeterminate: (configurationCode, compareTo, metadata) =>
			!getScopedExports(metadata, configurationCode).some(exportReport => isExportState(exportReport, "EXPORT_STATE:INTDETERMINATE"))
	},
	combination: {
		code: "COMBINATION",
		evaluate: (fieldValues, compareTo, metadata) => {
			const valueDictionaries = Object.values(fieldValues ?? {})[0];
			const sorted = [...Object.entries(valueDictionaries ?? {})]
				.sort(([aFieldId, a], [bFieldId, b]) => {
					const aCondition = metadata.rule.Conditions.find(c => c.Field.Id === parseInt(aFieldId));
					const bCondition = metadata.rule.Conditions.find(c => c.Field.Id === parseInt(bFieldId));

					return aCondition.Order - bCondition.Order ;
				})
				.map(([entryKey, entry]) => entry);

			const isInCombo = comboCheckDictionaryLookupCompare(sorted, metadata);

			return isInCombo;
		}
	},
	role: {
		code: "ROLE",
		equalTo: (fieldValues, compareTo, metadata) =>
			compareRoleVals(metadata, compareTo, (a, b) => a === b),
		notEqualTo: (fieldValues, compareTo, metadata) =>
			compareRoleVals(metadata, compareTo, (a, b) => a !== b)
	}
};

function comboCheckDictionaryLookupCompare(sortedValues, ruleMetadata) {
	const thisCombo = sortedValues
		.flatMap(arr => {
			if(arr.length === 0) {
				return [""];
			}
			return arr.map(v => !!v?.IsNotAvailable ? "$[NA]$" : (v?.DataId ?? v?.EntityId ?? ""));
		})
		.filter(v => v !== undefined);

	const lookup = ruleMetadata.rule.Blob.comboDictionary;

	const search = (node, index) => {
		if(index === thisCombo.length) {
			return Object.keys(node).length === 0
				|| (Object.hasOwnProperty.call(node, "") && search(node[""], index));
		}

		const value = thisCombo[index];
		if(value === "") {
			return Object.keys(node).some(key => search(node[key], index + 1));
		}

		return node[value] && search(node[value], index + 1);
	};

	const isValid = search(lookup, 0);

	return isValid === true;
}

class TRIGGER_METHODS_SERVICE {
	constructor($logger) {
		this.methods = {
			VISIBILITY: (field, operator, matched, rule, eventBus) => {
				const oldVisible = field.visible !== false;
				field.visible = !!((matched && operator.Matched === "SHOW") || (!matched && operator.Matched === "HIDE"));

				if(field.Section) {
					eventBus.trigger(EVENT_SECTION_FIELD_VISIBILTY_CHANGE(field.Section.Id));
				}

				field.initReadOnly = false;

				if(!field.visible && !!oldVisible) {
					field.value = [];
					eventBus.trigger(EVENT_CLEAR_VALUE(field.Id));
				}
				else if(!!field.visible && !oldVisible) {
					eventBus.trigger(EVENT_UNCLEAR_VALUE(field.Id));
				}

				return { visibilityChanged: oldVisible !== field.visible };
			},

			READONLY: (field, operator, matched, rule) => {
				field.isReadonly = !!((matched && operator.Matched === "ENABLE") || (!matched && operator.Matched === "DISABLE"));

				return { visibilityChanged: false };
			},

			DETAILS: (field, operator, matched, rule, eventBus) => {
				const suppressed = matched
					? operator.Matched === "DISABLED"
					: operator.NotMatched === "DISABLED";

				eventBus.trigger(EVENT_DETAILS_SUPPRESSION_TOGGLED, { suppressed });
			},

			REQUIRED: (field, operator, matched, rule) => {
				field.isRequired = (matched && operator.Matched === "REQUIRED");
			},

			HIGHLIGHT: (field, operator, matched, rule) => {
				// implement in the futute
			},

			LESSTHANOREQUALTO: (field, operator, matched, rule) => {
				if(!field.filters) {
					field.filters = {};
				}
				field.filters[CONDITION_OPERATORS.LESSTHANOREQUALTO] = [];

				if(rule.compareValues.length) {
					field.filters[CONDITION_OPERATORS.LESSTHANOREQUALTO] = rule.compareValues;
				}
				else {
					field.filters[CONDITION_OPERATORS.LESSTHANOREQUALTO] = null;
				}
			},

			GREATERTHANOREQUALTO: (field, operator, matched, rule) => {
				if(!field.filters) {
					field.filters = {};
				}
				field.filters[CONDITION_OPERATORS.GREATERTHANOREQUALTO] = [];
				if(rule.compareValues.length) {
					field.filters[CONDITION_OPERATORS.GREATERTHANOREQUALTO] = rule.compareValues;
				}
				else {
					field.filters[CONDITION_OPERATORS.GREATERTHANOREQUALTO] = null;
				}
			}
		};
	}
}
export const TRIGGER_METHODS = angular
	.module("vesl.services.trigger-methods", [])
	.service("triggerMethods", TRIGGER_METHODS_SERVICE).name;

export const TRIGGER_TYPES = {
	validation: "VALIDATION",
	userInterface: "USERINTERFACE",
	filter: "FILTER",
	export: "EXPORT"
};

export const UI_OPERATOR_TYPES = {
	visibility: "VISIBILITY",
	details: "DETAILS",
	filter: "FILTER",
	readonly: "READONLY"
};

export const ENGINE_TYPES = {
	RANGE: {
		function: "range",
		isAggregate: false
	},
	COMPARE: {
		function: "compare",
		isAggregate: false
	},
	"DETAILS:COUNT": {
		function: "detailsCount",
		isAggregate: true
	},
	ANY: {
		function: "any",
		isAggregate: true
	},
	VALUE: {
		function: "value",
		isAggregate: false
	},
	REQUIRED: {
		function: "required",
		isAggregate: false
	},
	CLASSIFICATION: {
		function: "classification",
		isAggregate: false
	},
	REGEX: {
		function: "regex",
		isAggregate: false
	},
	IDENTIFIER: {
		function: "identifier",
		isAggregate: false
	},
	ENVIRONMENT: {
		function: "environment",
		isAggregate: false,
		isMetadataEngine: true
	},
	UNIQUE: {
		function: "unique",
		isAggregate: true
	},
	INTERSECT: {
		function: "intersect",
		isAggregate: true
	},
	EMPTY: {
		function: "empty",
		isAggregate: false
	},
	NOTAVAILABLE: {
		function: "notAvailable",
		isAggregate: false
	},
	LENGTH: {
		function: "length",
		isAggregate: false
	},
	CHANGE: {
		function: "change",
		isAggregate: false
	},
	REMOTE: {
		function: "remote",
		isAggregate: false
	},
	CONTAINS: {
		function: "contains",
		isAggregate: false,
		isConditionAggregate: true
	},
	EXISTING: {
		function: "existing",
		isAggregate: false
	},
	CUSTOMENTRY: {
		function: "customEntry",
		isAggregate: false
	},
	PERIOD: {
		function: "period",
		isAggregate: false
	},
	STATE: {
		function: "state",
		isAggregate: false,
		isMetadataEngine: true
	},
	"EXPORT:SHOULD": {
		function: "exportShould",
		isAggregate: false,
		isMetadataEngine: true
	},
	"EXPORT:LATEST": {
		function: "exportLatest",
		isAggregate: false,
		isMetadataEngine: true
	},
	"EXPORT:PREVIOUSLY": {
		function: "exportPreviously",
		isAggregate: false,
		isMetadataEngine: true
	},
	"EXPORT:ANY": {
		function: "exportAny",
		isAggregate: false,
		isMetadataEngine: true
	},
	"EXPORT:NEVER": {
		function: "exportNever",
		isAggregate: false,
		isMetadataEngine: true
	},
	COMBINATION: {
		function: "combination",
		isAggregate: false,
		isConditionAggregate: true
	},
	LOCATION: {
		function: "location",
		isAggregate: false
	},
	ROLE: {
		function: "role",
		isAggregate: false,
		isMetadataEngine: true
	}
};

export const EVALUATION_PREPARERS = {
	Date: (evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("Date", fieldValues, condition.Scope);
		return dateEvaluator(evaluator, condition, metadata, mappedVals);
	},
	DateTime: (evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("DateTime", fieldValues, condition.Scope);
		return dateEvaluator(evaluator, condition, metadata, mappedVals);
	},
	Time: (evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("Time", fieldValues, condition.Scope);
		return timeEvaluator(evaluator, condition, metadata, mappedVals);
	},
	YearMonth:(evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("Date", fieldValues, condition.Scope);
		return dateEvaluator(evaluator, condition, metadata, mappedVals);
	},
	Number: (evaluator, condition, metadata, fieldValues) => {
		if(condition.Scope?.Code === "DATA") {
			return EVALUATION_PREPARERS.Selection(evaluator, condition, metadata, fieldValues);
		}
		const conditionFieldValues = filter(fieldValues, (v) => v.FieldId === condition.Field?.Id) || [];
		const compareToField = filter(fieldValues, (v) => v.FieldId === condition.CompareToField?.Id) || [];

		const mappedFieldVal = prepareValuesForTemplate("Number", conditionFieldValues, condition.Scope);
		const mappedCompareToVal = prepareValuesForTemplate("Number", compareToField, condition.Scope);

		const compareValues = {
			value: condition.Value === undefined ? (mappedCompareToVal.length > 0 ? mappedCompareToVal[0].value : undefined) : condition.Value,
			minValue: condition.MinValue,
			maxValue: condition.MaxValue
		};

		return evaluator(mappedFieldVal, compareValues, metadata);
	},
	Location: (evaluator, condition, metadata, fieldValues) => EVALUATION_PREPARERS.Number(evaluator, condition, metadata, fieldValues),
	Coordinate: (evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("Coordinate", fieldValues, condition.Scope);

		return evaluator(mappedVals, { value: condition.Value }, metadata);
	},
	Selection: (evaluator, condition, metadata, fieldValues) => {
		const values = prepareValuesForTemplate("Selection", fieldValues, condition.Scope);

		const compareToValue = find(values, (v) => v.FieldId === condition.CompareToField?.Id) || {};
		const vals = filter(values, (v) => v.FieldId === condition.Field?.Id) || [];
		const compareValues = {
			value: condition.Value || compareToValue,
			compareToFieldValue: compareToValue
		};

		return evaluator(vals, compareValues, metadata);
	},
	EntitySelector: (evaluator, condition, metadata, fieldValues) => {
		const values = prepareValuesForTemplate("EntitySelector", fieldValues, condition.Scope);
		const compareToValue = find(values, (v) => v.FieldId === condition.CompareToField?.Id) || {};

		const compareValues = {
			value: condition.Value,
			compareToFieldValue: compareToValue
		};

		return evaluator(values, compareValues, metadata);
	},
	EntityContactSelector: (evaluator, condition, metadata, fieldValues) => {
		const values = prepareValuesForTemplate("EntityContactSelector", fieldValues, condition.Scope);
		const compareToValue = find(values, (v) => v.FieldId === condition.CompareToField?.Id) || {};

		const compareValues = {
			value: condition.Value,
			compareToFieldValue: compareToValue
		};

		return evaluator(values, compareValues, metadata);
	},
	MultiSelect: (evaluator, condition, metadata, fieldValues) => {
		const values = prepareValuesForTemplate("MultiSelect", fieldValues, condition.Scope);

		return evaluator(values, {}, metadata);
	},
	Text: (evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("Text", fieldValues, condition.Scope);

		return evaluator(mappedVals, { value: condition.Value }, metadata);
	},
	StringMulti: (evaluator, condition, metadata, fieldValues) => {
		return EVALUATION_PREPARERS.Text(evaluator, condition, metadata, fieldValues);
	},
	TextArea: (evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("TextArea", fieldValues, condition.Scope);

		return evaluator(mappedVals, { value: condition.Value }, metadata);
	},
	Boolean: (evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("Boolean", fieldValues, condition.Scope);

		return evaluator(mappedVals, { value: condition.Value }, metadata);
	},
	Metadata: (evaluator, condition, metadata, conditionValues) => {
		return evaluator(conditionValues[0], !!condition.Value ? { value: condition.Value } : undefined, metadata);
	},
	SingleFile: (evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("File", fieldValues, condition.Scope);

		return evaluator(mappedVals, { value: condition.Value }, metadata);
	},
	ImageFile: (evaluator, condition, metadata, fieldValues) => {
		const mappedVals = prepareValuesForTemplate("File", fieldValues, condition.Scope);

		return evaluator(mappedVals, { value: condition.Value }, metadata);
	}
};

export const TEMPLATE_VALUE_PREPARERS = {
	Date: (fieldValues) => {
		return fieldValues;
	},
	DateTime: (fieldValues) => {
		return fieldValues;
	},
	Time: (fieldValues) => {
		return fieldValues
			.filter(fieldValue => fieldValue.IsNotAvailable || normalizeTimeValue(fieldValue.Time) !== undefined)
			.map(fieldValue => ({
				...fieldValue,
				Time: normalizeTimeValue(fieldValue.Time)
			}));
	},
	Number: (fieldValues) => {
		const validFieldValues = fieldValues.filter(v => v.IsNotAvailable
			|| (!isValueNullish(v.DataId ?? v.EntityId) && !isValueNullish(v.Number)));

		validFieldValues.forEach((field) => {
			if(field.Number !== undefined) {
				if(field.factor) {
					field.factoredValue = field.Number * field.factor;
				}
				else {
					field.factoredValue = field.Number;
				}
			}
		});

		let num = [];
		if(validFieldValues && validFieldValues.length > 0) {
			if(validFieldValues.length === 1) {
				num = [{ value: validFieldValues[0].factoredValue, IsNotAvailable: validFieldValues[0].IsNotAvailable }];
			}
			else {
				num = validFieldValues.map(fv => ({ value: fv.factoredValue, IsNotAvailable: fv.IsNotAvailable }));
			}
		}

		return num;
	},
	Location: (fieldValues) => TEMPLATE_VALUE_PREPARERS.Number(fieldValues),
	Coordinate: (fieldValues, scope) => {
		if(scope === CONDITION_SCOPES.Latitude) {
			return fieldValues.map(fv => ({
				...fv,
				Number: fv.Latitude
			}));
		}

		if(scope === CONDITION_SCOPES.Longitude) {
			return fieldValues.map(fv => ({
				...fv,
				Number: fv.Longitude
			}));
		}

		return fieldValues;
	},
	Selection: (fieldValues, scope) => {
		const values = filter(fieldValues, (fv) => {
			return (
				fv.IsDeleted != true
				&& (scope !== undefined || fv.Order !== undefined || fv.IsNotAvailable || fv.EntityId !== undefined || fv.DataId !== undefined || fv.String !== undefined)
			);
		})
			.map(fv => {
				let compareVal = fv.EntityId ?? fv.DataId ?? fv.Order ?? fv.String ?? (fv.IsNotAvailable === true ? "NOTAVAILABLE" : undefined);
				if(scope !== undefined) {
					compareVal = fv[scope];
				}
				return {
					...fv,
					compareVal,
					Code: fv._code,
					AllCodes: fv.allCodes,
					idRequired: true
				};
			});

		return values;
	},
	EntitySelector: (fieldValues, scope) => {
		const values = filter(fieldValues, (fv) => fv.IsDeleted != true &&
				(fv.EntityId != undefined || fv.IsNotAvailable || !!fv.String))
			.map(fv => {
				return {
					...fv,
					Code: fv._code,
					Classifications: fv.Classifications,
					IsNotAvailable: fv.IsNotAvailable,
					Identifiers: fv.Identifiers,
					AllCodes: fv.allCodes,
					idRequired: true
				};
			});

		return values;
	},
	EntityContactSelector: (fieldValues, scope) => {
		const values = filter(fieldValues,
			(fv) => {
				let isValid = fv.IsDeleted !== true;
				if(scope?.Code !== "CONATCT") {
					isValid = isValid && (fv.EntityId !== undefined || fv.IsNotAvailable);
				}
				if(scope?.Code !== "ENTITY") {
					isValid = isValid && fv.ContactId;
				}

				return isValid;
			})
			.map((fv) => {
				return {
					...fv,
					Code: fv._code,
					Classifications: fv.Classifications,
					IsNotAvailable: fv.IsNotAvailable,
					Identifiers: fv.Identifiers,
					idRequired: true
				};
			});

		return values;
	},
	MultiSelect: (fieldValues, scope) => {
		const values = filter(fieldValues, (fv) => (fv.IsDeleted === undefined || fv.IsDeleted === false) && (fv.DataId !== undefined || fv.EntityId !== undefined))
			.map(fv => fv.DataId ?? fv.EntityId);

		return values;
	},
	Text: (fieldValues, scope) => {
		return fieldValues;
	},
	TextArea: (fieldValues) => {
		return fieldValues;
	},
	Boolean: (fieldValues) => {
		const boolVal = fieldValues.length === 1 ? [fieldValues[0].Boolean] : [];
		return boolVal;
	},
	File: (fieldValues, scope) => {
		return fieldValues;
	}
};

function prepareValuesForTemplate(template, fieldValues, scope) {
	return TEMPLATE_VALUE_PREPARERS[template](fieldValues, scope);
}

export const TRIGGER_OPERATOR_CODES = {
	blockSubmit: "BLOCKSUBMIT",
	blockSave: "BLOCKSAVE",
	warning: "WARNING",
	highlight: "HIGHLIGHT",
	visibility: "VISIBILITY",
	filter: "FILTER",
	details: "DETAILS",
	readonly: "READONLY",
	remote: "ENQUEUE"
};

export const VALIDATION_OPERATORS = [
	{
		rules: [],
		code: TRIGGER_OPERATOR_CODES.blockSave,
		criticalLevel: 1,
		icon: "zmdi-alert-octagon",
		message: (count) => `${count} ${pluralize(count, "error")} must be resolved before saving.`,
		detailMessage: (count) => count && `${count} ${pluralize(count, "detail")} ${count === 1 ? "has an" : "have"} ${pluralize(count, "error")} which must be resolved before saving.`
	},
	{
		rules: [],
		code: TRIGGER_OPERATOR_CODES.blockSubmit,
		criticalLevel: 2,
		icon: "zmdi-alert-octagon",
		message: (count) => `${count} ${pluralize(count, "error")} must be resolved before completing.`,
		messageSubmitted: (count) => `This report has ${count} ${pluralize(count, "warning")}`,
		detailMessage: (count) => count && `${count} ${pluralize(count, "detail")} ${count === 1 ? "has an" : "have"} ${pluralize(count, "error")} which must be resolved before completing.`,
		detailMessageSubmitted: (count) => count && `This report has ${count} ${pluralize(count, "detail")} ${count === 1 ? "has a" : "have"} ${pluralize(count, "warning")}`
	},
	{
		rules: [],
		code: TRIGGER_OPERATOR_CODES.warning,
		criticalLevel: 3,
		icon: "zmdi-alert-triangle",
		message: (count) => `${count} ${pluralize(count, "warning")} must be reviewed before completing.`,
		messageSubmitted: (count) => `This report has ${count} ${pluralize(count, "warning")}`,
		detailMessage: (count) => count && `${count} ${pluralize(count, "detail")} ${count === 1 ? "has a" : "have"} ${pluralize(count, "warning")} which must be reviewed before completing.`,
		detailMessageSubmitted: (count) => count && `This report has ${count} ${pluralize(count, "detail")} ${count === 1 ? "has a" : "have"} ${pluralize(count, "warning")}`,
		isWarning: true
	}
];


